"use client";

import Link from "next/link";
import { getSiteContent } from "@/content";
import { type Locale, localizedPath } from "@/lib/site";

export function HomePage({ locale }: { locale: Locale }) {
  const t = getSiteContent(locale).pages.home;

  return (
    <div className="bg-background text-on-surface">
      <section className="home-banner-hero relative flex h-[760px] items-center overflow-hidden pb-20 pt-[128px] md:h-[780px] md:pb-32 md:pt-32 xl:h-[820px]">
        <div className="absolute inset-0 z-0">
          <div className="absolute left-1/4 top-1/4 h-[600px] w-[600px] rounded-full bg-secondary/10 blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 h-[400px] w-[400px] rounded-full bg-parcel-orange/5 blur-[100px]" />
        </div>
        <div className="relative z-10 mx-auto w-full max-w-container-max px-margin-mobile md:px-margin-tablet lg:px-margin-desktop">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="text-center lg:col-span-7 lg:text-left xl:col-span-6">
              <div className="hero-kicker mx-auto mb-8 inline-flex items-center gap-3 lg:mx-0">
                <span className="hero-kicker-dot" />
                <span>{t.hero.kicker}</span>
              </div>
              <h1 className="typo-hero-display-title hero-title mb-6 text-white md:mb-8">
                {t.hero.titleLines[0]}
                <br />
                <span className="text-parcel-orange">{t.hero.titleLines[1]}</span>
              </h1>
              <p className="typo-hero-subtitle hero-subtitle mx-auto mb-8 max-w-xl text-white/80 md:mb-10 lg:mx-0">
                {t.hero.subtitle}
              </p>
              <div className="mb-10 grid grid-cols-2 gap-x-2 gap-y-4 md:mb-12 md:gap-x-4 sm:grid-cols-4">
                {t.hero.badges.map((badge) => (
                  <div className="flex items-center justify-center gap-2 text-sm text-white/90 lg:justify-start" key={badge}>
                    <span className="hero-badge-icon material-symbols-outlined text-parcel-orange">check_circle</span>
                    <span>{badge}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col justify-center gap-4 sm:flex-row lg:justify-start">
                <Link
                  className="typo-button w-full rounded-full bg-parcel-orange px-10 py-4 text-center text-white shadow-xl shadow-parcel-orange/30 transition-all hover:scale-105 active:scale-95 sm:w-auto"
                  href={localizedPath(locale, "register")}
                >
                  {t.hero.primaryCta}
                </Link>
                <Link
                  className="typo-button w-full rounded-full border border-white/20 bg-white/10 px-10 py-4 text-center text-white backdrop-blur-md transition-all hover:bg-white/20 sm:w-auto"
                  href={localizedPath(locale, "tools/shipping-calculator")}
                >
                  {t.hero.secondaryCta}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-20 -mt-10 md:-mt-16">
        <div className="mx-auto max-w-container-max px-margin-mobile md:px-margin-tablet lg:px-margin-desktop">
          <div className="grid grid-cols-1 gap-8 rounded-[2rem] bg-white p-6 shadow-2xl md:p-8 lg:grid-cols-4">
            <div className="space-y-4 lg:col-span-1">
              <h3 className="typo-card-title text-primary">{t.quickTools.title}</h3>
              <div className="relative">
                <input className="home-query-input typo-input w-full rounded-2xl border-none bg-glacier-white py-4 pl-4 pr-12 transition-all focus:ring-2 focus:ring-ocean-cyan/20" placeholder={t.quickTools.placeholder} type="text" />
                <Link className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-xl bg-secondary text-white transition-colors hover:bg-primary" href={localizedPath(locale, "tools/tracking")} aria-label={t.quickTools.searchAria}>
                  <span className="material-symbols-outlined">search</span>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:col-span-3">
              {t.quickTools.entries.map(([icon, label, path]) => (
                <Link className="group flex flex-col items-center justify-center rounded-2xl bg-glacier-white p-4 transition-colors hover:bg-secondary/5 md:p-6" href={localizedPath(locale, path)} key={label}>
                  <span className="home-tool-icon material-symbols-outlined mb-3 text-secondary transition-transform group-hover:scale-110">{icon}</span>
                  <span className="typo-card-title text-primary">{label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-section-gap">
        <div className="mx-auto max-w-container-max px-margin-mobile md:px-margin-tablet lg:px-margin-desktop">
          <SectionHeading title={t.core.title} copy={t.core.copy} />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-4">
            {t.core.services.map(([icon, title, copy]) => (
              <article className="ui-card border border-border-pale bg-white p-8 transition-all hover:translate-y-[-4px] hover:shadow-xl" key={title}>
                <div className="ui-icon-tile ui-icon-tile-lg mb-6 bg-secondary/10">
                  <span className="material-symbols-outlined icon-card text-secondary">{icon}</span>
                </div>
                <h2 className="typo-card-title mb-4 text-primary">{title}</h2>
                <p className="typo-card-body text-on-surface-variant">{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-glacier-white py-12 md:py-section-gap">
        <div className="mx-auto max-w-container-max px-margin-mobile md:px-margin-tablet lg:px-margin-desktop">
          <div className="mb-10 flex flex-col items-center justify-between gap-4 md:mb-12 md:flex-row md:items-end">
            <div className="text-center md:text-left">
              <h2 className="typo-section-h2 mb-2 text-primary">{t.valueServices.title}</h2>
              <p className="typo-section-subtitle text-on-surface-variant">{t.valueServices.copy}</p>
            </div>
            <Link className="typo-status-tag flex items-center gap-1 text-secondary" href={localizedPath(locale, "services/introduction")}>
              {t.valueServices.linkLabel}
              <span className="material-symbols-outlined text-[14px]">chevron_right</span>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-5">
            {t.valueServices.services.map(([icon, title, copy]) => (
              <article className="ui-card border border-border-pale bg-white p-6 text-center transition-shadow hover:shadow-lg" key={title}>
                <span className="home-section-icon-lg material-symbols-outlined mb-4 text-parcel-orange">{icon}</span>
                <h3 className="typo-card-title mb-2 text-primary">{title}</h3>
                <p className="typo-card-body text-on-surface-variant">{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="overflow-hidden bg-white py-12 md:py-section-gap" id="process">
        <div className="mx-auto max-w-container-max px-margin-mobile md:px-margin-tablet lg:px-margin-desktop">
          <SectionHeading title={t.process.title} copy={t.process.copy} />
          <div className="relative">
            <div className="flow-dash-line absolute left-10 right-10 top-10 z-0 hidden md:block" />
            <div className="step-line-vertical md:hidden" />
            <div className="relative z-10 grid grid-cols-1 gap-12 md:grid-cols-2 md:gap-gutter lg:grid-cols-4">
              {t.process.steps.map(([icon, title, copy], index) => (
                <article className="group flex items-center gap-6 md:flex-col md:gap-0 md:text-center" key={title}>
                  <div className="relative mb-0 flex-shrink-0 md:mb-8">
                    <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-secondary text-white shadow-xl shadow-secondary/20 transition-transform group-hover:scale-110">
                      <span className="home-section-icon-lg material-symbols-outlined">{icon}</span>
                      <div className="absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full border-2 border-secondary bg-white font-bold text-secondary">{index + 1}</div>
                    </div>
                  </div>
                  <div>
                    <h3 className="typo-card-title mb-2 text-primary">{title}</h3>
                    <p className="typo-card-body text-on-surface-variant md:px-4">{copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="ambient-deep-bg relative overflow-hidden bg-primary py-12 text-white md:py-section-gap" id="estimation">
        <div className="relative z-10 mx-auto max-w-container-max px-margin-mobile md:px-margin-tablet lg:px-margin-desktop">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="w-full max-w-full rounded-[2rem] bg-white p-8 text-primary md:p-12">
              <h2 className="typo-section-h2 mb-8">{t.estimate.title}</h2>
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <HomeSelect label={t.estimate.destinationLabel} options={t.estimate.destinationOptions} />
                  <HomeSelect label={t.estimate.channelLabel} options={t.estimate.channelOptions} />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {t.estimate.fields.map(([label, placeholder]) => (
                    <HomeInput label={label} placeholder={placeholder} key={label} />
                  ))}
                </div>
                <Link className="home-estimate-cta typo-button block w-full rounded-2xl bg-secondary py-4 text-center text-white shadow-lg transition-all hover:bg-primary" href={localizedPath(locale, "tools/shipping-calculator")}>
                  {t.estimate.cta}
                </Link>
                <p className="typo-status-tag text-center text-on-surface-variant">{t.estimate.note}</p>
              </div>
            </div>
            <div className="text-center lg:pt-8 lg:text-left">
              <h2 className="typo-section-h2 mb-4">{t.estimate.sideTitle}</h2>
              <p className="typo-section-subtitle mb-10 text-white/70">{t.estimate.sideCopy}</p>
              <div className="mx-auto max-w-lg space-y-4 lg:mx-0">
                {t.estimate.sideRows.map(([title, copy]) => (
                  <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-left" key={title}>
                    <span className="material-symbols-outlined text-secondary">verified</span>
                    <div>
                      <div className="typo-card-title">{title}</div>
                      <div className="typo-card-body text-white/60">{copy}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-section-gap">
        <div className="mx-auto max-w-container-max px-margin-mobile md:px-margin-tablet lg:px-margin-desktop">
          <SectionHeading title={t.advantages.title} copy={t.advantages.copy} />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-4">
            {t.advantages.items.map(([icon, title, copy]) => (
              <article className="ui-card border border-border-pale bg-glacier-white p-8 transition-all hover:border-secondary" key={title}>
                <span className="home-section-icon-lg material-symbols-outlined mb-4 text-secondary">{icon}</span>
                <h3 className="typo-card-title mb-2 text-primary">{title}</h3>
                <p className="typo-card-body text-on-surface-variant">{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ambient-deep-bg relative overflow-hidden bg-[#001026] py-12 md:py-section-gap" id="global-routes">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(#ffffff_1px,transparent_1px)] bg-[length:30px_30px]" />
        </div>
        <div className="relative z-10 mx-auto max-w-container-max px-margin-mobile md:px-margin-tablet lg:px-margin-desktop">
          <SectionHeading dark title={t.routes.title} copy={t.routes.copy} />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {t.routes.items.map(([icon, title, coverage, channel, eta]) => (
              <article className="ui-card group border border-white/10 bg-white/5 p-8 backdrop-blur-md transition-all hover:bg-white/10" key={title}>
                <div className="ui-icon-tile ui-icon-tile-md mb-6 bg-secondary/20 text-secondary transition-transform group-hover:scale-110">
                  <span className="material-symbols-outlined">{icon}</span>
                </div>
                <h3 className="typo-card-title mb-6 text-white">{title}</h3>
                <div className="typo-card-body space-y-4">
                  <RouteRow label={t.routes.coverageLabel} value={coverage} />
                  <RouteRow label={t.routes.channelLabel} value={channel} />
                  <div>
                    <div className="mb-1 text-white/40">{t.routes.etaLabel}</div>
                    <div className="typo-card-title text-secondary">{eta}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-12 md:py-section-gap" id="guarantee">
        <div className="mx-auto max-w-container-max px-margin-mobile md:px-margin-tablet lg:px-margin-desktop">
          <SectionHeading title={t.guarantee.title} copy={t.guarantee.copy} />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-4">
            {t.guarantee.cards.map(([icon, title, copy], index) => (
              <article className="ui-card border border-border-pale bg-glacier-white p-8 text-center" key={title}>
                <div className={`ui-icon-round ui-icon-tile-xl mx-auto mb-6 ${homeGuaranteeIconClass(index).bg}`}>
                  <span className={`material-symbols-outlined icon-feature ${homeGuaranteeIconClass(index).color}`}>{icon}</span>
                </div>
                <h3 className="typo-card-title mb-4 text-primary">{title}</h3>
                <p className="typo-card-body text-on-surface-variant">{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-section-gap" id="faq">
        <div className="mx-auto max-w-container-max px-margin-mobile md:px-margin-tablet lg:px-margin-desktop">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3 lg:gap-16">
            <div className="lg:col-span-2">
              <div className="mb-8 flex items-center justify-between gap-4">
                <h2 className="typo-section-h2 text-primary">{t.guide.title}</h2>
                <Link className="typo-status-tag inline-flex items-center gap-1 text-secondary" href={localizedPath(locale, "guide")}>
                  {t.guide.moreGuides}
                  <span className="material-symbols-outlined text-[14px]">chevron_right</span>
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {t.guide.cards.map(([title, copy, path]) => (
                  <GuideCard href={localizedPath(locale, path)} title={title} copy={copy} readMore={t.guide.readMore} key={title} />
                ))}
              </div>
            </div>
            <div>
              <h2 className="typo-section-h2 mb-8 text-primary">{t.guide.faqTitle}</h2>
              <div className="space-y-4">
                {t.guide.faqs.map(([question, answer]) => (
                  <HomeFAQ question={question} answer={answer} key={question} />
                ))}
              </div>
              <Link className="typo-status-tag mt-6 inline-flex items-center gap-1 text-secondary" href={localizedPath(locale, "faq")}>
                {t.guide.moreQuestions}
                <span className="material-symbols-outlined text-[14px]">chevron_right</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-glacier-white py-12 md:py-section-gap" id="contact-us">
        <div className="mx-auto max-w-container-max px-margin-mobile md:px-margin-tablet lg:px-margin-desktop">
          <SectionHeading title={t.contact.title} copy={t.contact.copy} />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
            {t.contact.cards.map(([icon, title, action, bg, color]) => (
              <Link className="ui-card flex flex-col items-center border border-border-pale bg-white p-6 text-center" href={localizedPath(locale, "contact")} key={title}>
                <div className={`ui-icon-round ui-icon-tile-md mb-4 ${bg} ${color}`}>
                  <span className="material-symbols-outlined">{icon}</span>
                </div>
                <div className="typo-card-title text-primary">{title}</div>
                <span className="typo-status-tag mt-2 border-b border-secondary text-secondary">{action}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHeading({ title, copy, dark = false }: { title: string; copy: string; dark?: boolean }) {
  return (
    <div className="mb-10 text-center md:mb-16">
      <h2 className={`typo-section-h2 mb-4 ${dark ? "text-white" : "text-primary"}`}>{title}</h2>
      <p className={`typo-section-subtitle mx-auto max-w-3xl ${dark ? "text-white/60" : "text-on-surface-variant"}`}>{copy}</p>
    </div>
  );
}

function HomeSelect({ label, options }: { label: string; options: readonly string[] }) {
  return (
    <label className="relative block">
      <span className="typo-form-label mb-2 block uppercase text-on-surface-variant">{label}</span>
      <select className="home-select-trigger w-full cursor-pointer appearance-none rounded-2xl bg-glacier-white py-3 pl-4 pr-12 transition-all" defaultValue={options[0] ?? ""}>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <span className="home-select-arrow pointer-events-none material-symbols-outlined absolute right-4 top-[calc(50%+12px)] -translate-y-1/2" aria-hidden="true">
        expand_more
      </span>
    </label>
  );
}

function HomeInput({ label, placeholder }: { label: string; placeholder: string }) {
  return (
    <label>
      <span className="typo-form-label mb-2 block uppercase text-on-surface-variant">{label}</span>
      <input className="home-estimate-input w-full rounded-2xl border-none bg-glacier-white py-3" placeholder={placeholder} type="number" />
    </label>
  );
}

function RouteRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="mb-1 text-white/40">{label}</div>
      <div className="text-white/90">{value}</div>
    </div>
  );
}

function GuideCard({ title, copy, href, readMore }: { title: string; copy: string; href: string; readMore: string }) {
  return (
    <Link className="ui-card group border border-border-pale bg-white p-6 transition-all hover:border-secondary" href={href}>
      <h3 className="typo-card-title mb-2 text-primary group-hover:text-secondary">{title}</h3>
      <p className="typo-card-body mb-4 text-on-surface-variant">{copy}</p>
      <span className="typo-status-tag flex items-center gap-1 uppercase text-secondary">
        {readMore}
      </span>
    </Link>
  );
}

function homeGuaranteeIconClass(index: number) {
  const classes = [
    { bg: "bg-success/10", color: "text-success" },
    { bg: "bg-secondary/10", color: "text-secondary" },
    { bg: "bg-parcel-orange/10", color: "text-parcel-orange" },
    { bg: "bg-primary/5", color: "text-primary" }
  ];

  return classes[index] ?? classes[0];
}

function HomeFAQ({ question, answer }: { question: string; answer: string }) {
  return (
    <details className="group border-b border-border-pale pb-4">
      <summary className="typo-card-title flex cursor-pointer list-none items-center justify-between text-primary">
        {question}
        <span className="material-symbols-outlined transition-transform group-open:rotate-180">expand_more</span>
      </summary>
      <p className="typo-card-body mt-4 text-on-surface-variant">{answer}</p>
    </details>
  );
}
